import React, { memo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Types
import { User } from 'Shared/constants/types';

// Constants
import ColorConstants from 'Wpds/colors/ColorConstants';

// Helpers
import { sanitizeFullName } from 'Shared/helpers';
import { handleLaunchExternalLink } from 'Shared/utils/external-link';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';
import { useBaProfile } from 'Shared/utils/hooks/use-ba-profile';
import { useBaTracking } from 'Shared/tracking/use-ba-tracking';
import { useCreateOrSendBa } from 'Shared/utils/hooks/use-create-or-send-ba';
import { useApp } from 'Shared/utils/hooks/use-app';

// Components
import { WpAvatar } from 'Wpds/components/avatar';
import WorkspacePersonalAvatar from './WorkspacePersonalAvatar';
import UserProfilePopover from 'Dashboard/components/UserProfilePopover/UserProfilePopover';
import WorkspaceInviteButton from './WorkspaceInviteButton';
import WpButton from 'Wpds/components/button/WpButton';
import { WpIcon } from 'Wpds/components/icon';

// Styles
import './WorkspacePageTitleForPI.scss';

const WorkspacePageTitleForPI = () => {
  const { slug: currentUserSlug } = useCurrentUser();
  const { isPersonal, isOneOnOne, otherUserInOneOnOne, users, name: workspaceName } = useWorkspace();
  const { featureFlagShowRelationshipGuideInWorkspace } = useFlags();
  const { isZoom } = useApp();

  const currentUserInWorkspace = users.find(u => u.slug === currentUserSlug);

  // if other user in one on one workspace hasn't been invited,
  // stub out a fake user object to render the user details
  const colleague =
    otherUserInOneOnOne ||
    ({
      fullName: workspaceName,
      avatar: {
        color: ColorConstants.purple[900],
        initials: workspaceName.charAt(0).toUpperCase(),
        profilePictureUrl: '',
      },
    } as User);

  const baseClassName = 'WorkspacePageTitleForPI';
  const UserContainer = ({ user }: { user: User }) => {
    const { referencePatternName, hasPendingAssesmentId, hasPendingBa, isFetching } = useBaProfile({ user });

    const isCurrentUser = user.slug === currentUserSlug;
    const name = isPersonal ? workspaceName : sanitizeFullName(user.fullName);
    const showInviteButton = isOneOnOne && !isCurrentUser && !otherUserInOneOnOne;
    const showResendInviteButton =
      isOneOnOne && !isCurrentUser && !otherUserInOneOnOne?.confirmedAt && !!otherUserInOneOnOne?.confirmationSentAt;
    const { trackSendAssessmentButtonClick, trackTakeAssessmentButtonClick } = useBaTracking();
    const { sendBaEmail } = useCreateOrSendBa({ user: user, isTakingBaForSelf: false });
    const { getBaUrl } = useCreateOrSendBa({ user: user, isTakingBaForSelf: true });

    const handleSendBaClick = async () => {
      trackSendAssessmentButtonClick();
      await sendBaEmail();
    };

    const handleTakeBaClick = async () => {
      trackTakeAssessmentButtonClick();
      const url = await getBaUrl();
      handleLaunchExternalLink({ href: url });
    };

    const baSubtextTemplate = () => {
      if (showInviteButton || showResendInviteButton) {
        return (
          <WorkspaceInviteButton
            inviteBtnText="Invite to Perform"
            inviteBtnIcon="paper-plane"
            btnVariant="link"
            includePendingMessage
          />
        );
      }
      if (isPersonal) {
        return (
          <p className={`${baseClassName}__user__details__reference-pattern`}>
            {referencePatternName || 'Your private space'}
          </p>
        );
      }

      if (referencePatternName) {
        return <p className={`${baseClassName}__user__details__reference-pattern`}>{referencePatternName}</p>;
      }

      if (!featureFlagShowRelationshipGuideInWorkspace || isFetching) return null;
      if (isZoom) return null;

      if (hasPendingAssesmentId || hasPendingBa) {
        return (
          <p className={`${baseClassName}__user__details__reference-pattern`}>
            <WpIcon name="hourglass" /> Pending
          </p>
        );
      }

      if (isCurrentUser) {
        return (
          <WpButton onClick={handleTakeBaClick} variant="link">
            <WpIcon name="external-link" />
            Take the Behavioral Assessment
          </WpButton>
        );
      }

      if (!isCurrentUser) {
        return (
          <WpButton onClick={handleSendBaClick} variant="link">
            <WpIcon name="paper-plane" />
            Send Behavioral Assessment
          </WpButton>
        );
      }

      return null;
    };

    return (
      <UserProfilePopover className={`${baseClassName}__user`} user={user}>
        <div className={`${baseClassName}__user__avatar`}>
          {isPersonal ? (
            <WorkspacePersonalAvatar />
          ) : (
            <WpAvatar avatar={user.avatar} size="lg" referencePattern={referencePatternName} showReferencePatternIcon />
          )}
        </div>
        <div
          className={`${baseClassName}__user__details ${
            referencePatternName ? `${baseClassName}__user__details--with-reference-pattern` : ''
          }`}
        >
          <h3 className={`${baseClassName}__user__details__name`}>{name}</h3>
          {baSubtextTemplate()}
        </div>
      </UserProfilePopover>
    );
  };

  return (
    <div className={baseClassName}>
      {isOneOnOne && <UserContainer user={colleague} />}
      <UserContainer user={currentUserInWorkspace} />
    </div>
  );
};

export default memo(WorkspacePageTitleForPI);
